import {
	FieldValues,
	FieldPath,
	useController,
	UseControllerProps,
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { FormControl, FormHelperText, TextField } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import dayjs from 'dayjs';
import {
	API_DATE_FORMAT,
	DISPLAY_DATE_FORMAT,
} from '../../../utils/dateHelper';

interface AppDatePickerProps {
	label?: string;
	minimumDate?: string;
	maximumDate?: string;
	placeholder?: string;
	disabled?: boolean;
	formatDate?: string;
}

export default function AppDatePicker<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	label,
	minimumDate,
	maximumDate,
	placeholder,
	disabled,
	...props
}: AppDatePickerProps & UseControllerProps<TFieldValues, TName>) {
	const { field, fieldState } = useController(props);
	const { formatDate } = props;
	const errorMessage = fieldState.error?.message;

	return (
		<DatePicker
			disabled={disabled}
			{...field}
			inputFormat={DISPLAY_DATE_FORMAT}
			minDate={
				minimumDate === '' || minimumDate === undefined
					? undefined
					: dayjs(
							minimumDate,
							formatDate ? formatDate : API_DATE_FORMAT
					  ).format()
			}
			maxDate={
				maximumDate === '' || maximumDate === undefined
					? undefined
					: dayjs(
							maximumDate,
							formatDate ? formatDate : API_DATE_FORMAT
					  ).format()
			}
			value={
				field.value === '' || field.value === undefined || field.value === null
					? null
					: dayjs(
							field.value,
							formatDate ? formatDate : API_DATE_FORMAT
					  ).format()
			}
			onChange={(value) => {
				if (value === null) {
					field.onChange(null);
				} else
					field.onChange(
						dayjs(value).format(formatDate ? formatDate : API_DATE_FORMAT)
					);
			}}
			components={{
				OpenPickerIcon: fieldState.error
					? EventBusyOutlinedIcon
					: CalendarTodayOutlinedIcon,
			}}
			renderInput={(params) => {
				const formatPlaceholder = params.inputProps?.placeholder;
				return (
					<FormControl fullWidth={true} error={!!errorMessage}>
						<TextField
							{...params}
							variant='filled'
							inputProps={{
								...params.inputProps,
								placeholder: placeholder ? placeholder : formatPlaceholder,
							}}
							label={label}
							error={!!errorMessage}
							fullWidth
						/>
						<FormHelperText>{errorMessage}</FormHelperText>
					</FormControl>
				);
			}}
		/>
	);
}
